import { useEffect, useLayoutEffect, useRef } from 'react';
import { useUserOnboardingStore } from '@/store/useUserOnboardingStore';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import ContactInfoForm from '@/modules/onboarding/user/A_ContactInfoForm';
import DemographicsSetup from '@/modules/onboarding/user/B_DemographicsSetup';
import ProfileOverview from '@/modules/onboarding/user/C_ProfileOverview';
import { useAppStore } from '@/store/useAppStore';
import useToast from '@/hooks/useToast';
import { mixpanelEvents } from '@/constants/mixpanel';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import roles from '@/constants/roles';
import { useMutation } from '@tanstack/react-query';
import { skipDemographicsPost } from '@/services/api/requests/user';
import { TCustomAxiosError } from '@/services/api';

// 0 - ContactInfoForm (Contact Info)
// 1 - Demographics (Demographics Info)
// 2 - ProfileOverview (Picture upload)

const OnboardingUser = () => {
  const toast = useToast();
  const router = useRouter();
  const { trackEvent } = useTelemetry();

  const {
    onboardingStatuses: { isUserContactInfoCompleted, isUserDemographicsCompleted },
    user,
    isEulaAccepted,
    setUser,
  } = useAppStore();
  const { step, setStep } = useUserOnboardingStore();
  const viewingDemographicsFromHistory = useRef(false);
  const skip = useMutation({
    mutationFn: skipDemographicsPost,
    onSuccess: (timestamp) => {
      const currentUser = useAppStore.getState().user;
      if (currentUser && currentUser.user_id === user?.user_id) setUser({ ...currentUser, user_metadata: { ...currentUser.user_metadata, demographicsSkippedAt: timestamp } });
    },
    onError: (error: TCustomAxiosError) => {
      viewingDemographicsFromHistory.current = true;
      setStep(1);
      toast({ title: 'Error while skipping demographics', status: 'error' }, error);
    },
  });

  const isMember = !!user && !user.roles.includes(roles.superAdmin) && !(user.roles.includes(roles.contentCreator) && user.roles.length === 1);
  const isContactInfoForm = step === 0;
  const isDemographicsSetup = step === 1;

  useLayoutEffect(() => {
    if (viewingDemographicsFromHistory.current) return;
    const isOnboardingCompleted = isUserContactInfoCompleted && isEulaAccepted && isUserDemographicsCompleted;
    if (isOnboardingCompleted) {
      setStep(2);

      return;
    }

    setStep(isUserContactInfoCompleted && isEulaAccepted ? 1 : 0);
  }, [isUserContactInfoCompleted, isUserDemographicsCompleted, isEulaAccepted, setStep]);

  useEffect(() => router.history.subscribe(({ location, action }) => {
    if (location.pathname !== '/onboarding/user' || !['BACK', 'FORWARD', 'GO'].includes(action.type)) return;
    const historyStep = (location.state as { demographicsSkipStep?: number }).demographicsSkipStep;
    if (historyStep === 1 || historyStep === 2) {
      viewingDemographicsFromHistory.current = historyStep === 1;
      setStep(historyStep);
    }
  }), [router, setStep]);

  if (isContactInfoForm) {
    return (
      <ContactInfoForm
        onSubmitSuccess={() => {
          if (isUserDemographicsCompleted) {
            toast({
              title: 'Contact info has been successfully updated',
              description: 'You can now start using the platform',
              status: 'info',
            });

            trackEvent(mixpanelEvents.USER_SIGN_UP_DONE, user?.user_metadata);
          }
          if (!isUserDemographicsCompleted) {
            toast({
              title: 'Contact info has been successfully updated',
              description: 'You will be redirected to setup your demographics info',
              status: 'info',
            });
            setStep(1);
            return;
          }

          setStep(2);
        }}
      />
    );
  }

  if (isDemographicsSetup) {
    return (
      <DemographicsSetup
        isMember={isMember}
        isSkipping={skip.isPending}
        onSkip={() => {
          if (!user || skip.isPending) return;
          viewingDemographicsFromHistory.current = false;
          const { href, state } = router.history.location;
          router.history.replace(href, { ...state, demographicsSkipStep: 1 });
          // TanStack batches history writes; commit the current entry before pushing the overview.
          router.history.flush();
          router.history.push(href, { ...state, demographicsSkipStep: 2 });
          setStep(2);
          skip.mutate();
        }}
        onSubmitSuccess={(updatedUser) => {
          viewingDemographicsFromHistory.current = false;
          setUser(updatedUser);
          trackEvent(mixpanelEvents.USER_DEMOGRAPHICS_ONBOARDING_DONE, updatedUser.user_metadata);
          setStep(2);
        }}
      />
    );
  }

  return <ProfileOverview setOnboardingStep={(nextStep) => {
    viewingDemographicsFromHistory.current = nextStep === 1;
    setStep(nextStep);
  }} isSkippingDemographics={skip.isPending} />;
};

export const Route = createFileRoute('/_authenticated/_onboarding/onboarding/user')({
  staticData: { title: 'Onboarding' },
  component: OnboardingUser,
  beforeLoad: async ({ context: { auth } }) => {
    const isOrgAdmin = auth?.isAdmin;
    const {
      onboardingStatuses: { isOrganizationOnboardingCompleted },
    } = useAppStore.getState();

    const shouldRedirectToOrgOnboarding = isOrgAdmin && !isOrganizationOnboardingCompleted;
    if (shouldRedirectToOrgOnboarding) {
      throw redirect({
        to: '/onboarding/organization',
      });
    }
  },
});
